const MEASUREMENT_ID = import.meta.env.VITE_GA_MEASUREMENT_ID;
const CONSENT_KEY = 'ga_consent';

let initialized = false;

// ─── Consent ─────────────────────────────────────────────────────────────────

export const hasConsent = () => localStorage.getItem(CONSENT_KEY) === 'accepted';
export const hasDecided = () => !!localStorage.getItem(CONSENT_KEY);

export const giveConsent = () => {
  localStorage.setItem(CONSENT_KEY, 'accepted');
  initGA();
};

export const declineConsent = () => {
  localStorage.setItem(CONSENT_KEY, 'declined');
};

// ─── Initialisation ───────────────────────────────────────────────────────────

export const initGA = () => {
  if (initialized || !import.meta.env.PROD || !MEASUREMENT_ID) return;

  const script = document.createElement('script');
  script.async = true;
  script.src = `https://www.googletagmanager.com/gtag/js?id=${MEASUREMENT_ID}`;
  document.head.appendChild(script);

  window.dataLayer = window.dataLayer || [];
  window.gtag = function () { window.dataLayer.push(arguments); };
  window.gtag('js', new Date());
  window.gtag('config', MEASUREMENT_ID, { send_page_view: false });

  initialized = true;
};

const isEnabled = () => initialized && typeof window.gtag === 'function';

// ─── User ─────────────────────────────────────────────────────────────────────

export const setGAUser = (userId) => {
  if (!isEnabled()) return;
  window.gtag('config', MEASUREMENT_ID, { user_id: userId });
};

// ─── Page views ───────────────────────────────────────────────────────────────

export const trackPageView = (path) => {
  if (!isEnabled()) return;
  window.gtag('config', MEASUREMENT_ID, { page_path: path });
};

// ─── E-commerce events ────────────────────────────────────────────────────────

export const trackViewItem = (product) => {
  if (!isEnabled()) return;
  window.gtag('event', 'view_item', {
    currency: 'GBP',
    value: product.price,
    items: [
      {
        item_id: product._id,
        item_name: product.name,
        price: product.price,
        item_category: product.category?.name || product.category || '',
      },
    ],
  });
};

export const trackAddToCart = (product, quantity = 1) => {
  if (!isEnabled()) return;
  window.gtag('event', 'add_to_cart', {
    currency: 'GBP',
    value: product.price * quantity,
    items: [
      {
        item_id: product._id,
        item_name: product.name,
        price: product.price,
        quantity,
        item_category: product.category?.name || product.category || '',
      },
    ],
  });
};

export const trackBeginCheckout = (groupedCart, totalAmount) => {
  if (!isEnabled()) return;
  window.gtag('event', 'begin_checkout', {
    currency: 'GBP',
    value: totalAmount,
    items: groupedCart.map((group) => ({
      item_id: group.product._id,
      item_name: group.product.name,
      price: group.product.price,
      quantity: group.inventoryItems.length,
    })),
  });
};

export const trackPurchase = (order) => {
  if (!isEnabled()) return;
  window.gtag('event', 'purchase', {
    transaction_id: order.id,
    currency: 'GBP',
    value: order.totalAmount,
    shipping: order.shippingCost || 0,
    items: order.items.map((item) => ({
      item_id: item.productId || item.name,
      item_name: item.name,
      price: item.price,
      quantity: item.quantity,
    })),
  });
};

// ─── Custom events ────────────────────────────────────────────────────────────

export const trackCheckoutError = (errorType, message) => {
  if (!isEnabled()) return;
  window.gtag('event', 'checkout_error', {
    error_type: errorType || 'unknown',
    error_message: message || '',
  });
};
